/**
 * Caliu Pro client-side gating constants + helpers.
 *
 * Mirrors the Swift app's `SubscriptionManager` free-tier sets exactly
 * (`apps/swift/Sources/Caliu/Services/SubscriptionManager.swift`). The single
 * `isPro` value comes from the worker via `GET /subscription` (see
 * `store/subscription.ts`); grandfathered + paying users get everything.
 * Checkout runs through RevenueCat Web Billing (`lib/purchases.ts`).
 */

import type { TFunction } from 'i18next'
import type { Entitlement } from '@caliu/types'
import { formatDate } from './format'

/**
 * The renewal line of the subscribed surfaces (Swift `ProStatus.text`): a
 * lifetime plan never renews or ends, whatever `expiresAt` says; so does an
 * open-ended grant (grandfathered / promotional with no expiry). null when
 * nothing says what backs the entitlement (source `none`: unlocked before the
 * server's first answer, or right after a checkout), rather than a guess.
 */
export function proStatusLine(
  t: TFunction,
  e: Pick<Entitlement, 'expiresAt' | 'willRenew' | 'source'> & { plan: Entitlement['plan'] },
): string | null {
  if (e.plan === 'lifetime') return t('pro.lifetime')
  if (e.expiresAt) {
    return e.willRenew
      ? t('pro.renewsOn', { date: formatDate(e.expiresAt) })
      : t('pro.endsOn', { date: formatDate(e.expiresAt) })
  }
  return e.source === 'grandfathered' || e.source === 'promotional' ? t('pro.lifetime') : null
}

/** Caliu (light Caliu / dark Bosc), Pissarra, Turquesa stay free; rest are Pro.
 *  Gating is by theme id, so each free id unlocks its light + dark variants. */
export const FREE_THEME_IDS = ['caliu', 'pissarra', 'turquesa'] as const

/** Two fonts per category + the system font stay free. */
export const FREE_FONT_IDS = [
  'plus-jakarta-sans',
  'source-sans-3', // sans
  'lora',
  'crimson-pro', // serif
  'jetbrains-mono',
  'inconsolata', // mono
  'system-sf', // OS default, always free
] as const

/** Plain-text and Markdown export stay free; every rich format (HTML, RTF,
 *  ePub, TextPack) and all bulk export are Pro. */
export const FREE_EXPORT_FORMATS = ['markdown', 'txt'] as const

/** Fallback display pricing when the RevenueCat offering hasn't loaded. The
 *  paywall prefers the live offering prices (like Swift). */
export const PRO_PRICE_MONTHLY = '$2.99'
export const PRO_PRICE_YEARLY = '$17.99'

export function canUseTheme(isPro: boolean, id: string): boolean {
  return isPro || (FREE_THEME_IDS as readonly string[]).includes(id)
}

export function canUseFont(isPro: boolean, id: string): boolean {
  return isPro || (FREE_FONT_IDS as readonly string[]).includes(id)
}

export function canUseExportFormat(isPro: boolean, format: string): boolean {
  return isPro || (FREE_EXPORT_FORMATS as readonly string[]).includes(format)
}
